import uuid from "react-uuid";
import { base64ToImage, blobUrlToFile } from "../utils/utils";
import { imageUploadToS3 } from "./AwsBackEndService";
import {
  MediaKind,
  keyPrefix,
  mediaSrcAction,
  srcForStorage,
} from "../utils/self.serve.media";

/**
 * Uploads landing-page instruction media to S3 and rewrites the HTML to store object keys
 * (ELS-639), mirroring `MessageService.getMessageData` for the message centre.
 *
 * The editor gives us base64 images and `blob:` video previews. Those cannot be stored: the
 * column is `TEXT` (64KB) and a single inlined image exceeds it. So each media node is uploaded
 * and its `src` replaced with the S3 key before the class is saved.
 *
 * Reading back is **not** symmetrical with the message centre. There, the browser swaps keys for
 * signed URLs via an authenticated endpoint; the Self-Serve landing page is anonymous, so the
 * server substitutes them instead — see `SsClassService.signLandingMedia`.
 *
 * @see docs/edsims/modules/21-self-serve-enrollment.md
 */

/** `<img>` for images; the editor emits uploaded video as an `<iframe>`, older content as `<video>`. */
const MEDIA_SELECTORS: { selector: string; kind: MediaKind }[] = [
  { selector: "img", kind: "image" },
  { selector: "iframe", kind: "video" },
  { selector: "video", kind: "video" },
];

const uploadLocalSrc = async (src: string, kind: MediaKind): Promise<string> => {
  // Videos are blob: preview URLs — upload the file bytes like Message Centre FileUploader.
  // Images are already data: URLs and still go through base64ToImage.
  const file = src.startsWith("blob:")
    ? await blobUrlToFile(src, uuid())
    : base64ToImage(src, uuid());
  const uploaded = await imageUploadToS3(keyPrefix(kind), file);
  return uploaded.fileKey;
};

/**
 * Returns the instructions HTML with every media `src` reduced to a stored S3 object key.
 *
 * A failed upload leaves that node's `src` untouched rather than dropping the media: the admin
 * keeps what they authored and can retry, instead of silently saving a broken page. Since an
 * un-uploaded `src` is still base64, the caller must treat this as a failure — see
 * {@link containsLocalMedia}.
 */
export const withUploadedMedia = async (html: string): Promise<string> => {
  if (!html) {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, "text/html");

  for (const { selector, kind } of MEDIA_SELECTORS) {
    for (const node of Array.from(doc.querySelectorAll(selector))) {
      const src = node.getAttribute("src");
      const action = mediaSrcAction(src, kind);

      if (action === "upload") {
        try {
          node.setAttribute("src", await uploadLocalSrc(src as string, kind));
        } catch (e) {
          console.error("Landing media upload failed", e);
        }
        continue;
      }
      const stored = srcForStorage(src, kind);
      if (stored) {
        node.setAttribute("src", stored);
      }
    }
  }
  return doc.body.innerHTML;
};

/**
 * True when media is still embedded in the HTML rather than uploaded.
 *
 * Guards the save: base64 in the payload would either overflow the `TEXT` column or be truncated
 * into corrupt markup, and both are worse than refusing to save.
 */
export const containsLocalMedia = (html: string): boolean => {
  if (!html) {
    return false;
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  return MEDIA_SELECTORS.some(({ selector, kind }) =>
    Array.from(doc.querySelectorAll(selector)).some(
      (node) => mediaSrcAction(node.getAttribute("src"), kind) === "upload"
    )
  );
};
